import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import styles from './styles.module.scss';

// assets

// services
import UserService from '../../../../services/users/user';
import ErrorService from '../../../../services/general/error';

// props
import { IUserResourceShortProps } from '../../../../props/users/user';
import { IPaginationResourceShort } from '../../../../props/general';

// components
import { ActionButton, CommandBar, IColumn, ICommandBarItemProps, IContextualMenuItem, Persona, PersonaSize, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack, TooltipHost } from '@fluentui/react';
import { useStore } from '../../../../stores/root';
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import Text from './../../../typography/text';
import Label from '../../../typography/label';
import Tag, { TTagVariant } from '../../../uiframeworks/tag';
import { IOrderResourceProps, IOrderResourceShortProps } from '../../../../props/orders/order';
import GeneralService from '../../../../services/general';
import FileService from '../../../../services/general/file';
import DeliveredGoodsType from '../../../../manifests/deliveredGoodsType';
import OrderService from '../../../../services/orders/order';
import OrderForm from '../form';
import OrderListFilterButton, { TFilterBy } from './filter';
import { FilterOptionProps } from '../../../uiframeworks/filters/panel';
import SelectedFilter from '../../../uiframeworks/filters/selected';
import Pagination from '../../../uiframeworks/pagination';
import GlobalConfig from '../../../../config';
import { IOrderStatusResourceShortProps } from '../../../../props/orders/orderStatus';
import OrderStatusService from '../../../../services/orders/orderStatus';
import PivotButton from '../../../uiframeworks/buttons/pivot';
import PermissionsService from '../../../../services/permissions';
import Permissions from '../../../../permissions';

interface IOrdersListProps {
    qs?: string[];
    orders?: IOrderResourceShortProps[];
    pagination?: IPaginationResourceShort;
    columns?: TOrderColumn[];
    hideSearch?: boolean;
    hideCommandBar?: boolean;
    hideCreateOrder?: boolean;
    hideFilter?: boolean;
    hideStatusFilter?: boolean;
    filters?: TFilterBy[];
    variant?: 'card' | 'plain';
    onSearch?(keyword: string): void; 
}

export type TOrderColumn = 'profit' | 'orderNumber' | 'actions' | 'sales' | 'customer' | 'batch' | 'deliveryType' | 'deliveryDetails' | 'price' | 'orderStatus' | 'paymentStatus' | 'loads';

const OrdersList: React.FC<IOrdersListProps> = (props: IOrdersListProps) => {
    const shownColumns = props.columns || [
        'orderNumber',
        'actions',
        'customer',
        'batch',
        'deliveryType',
        'deliveryDetails',
        'price',
        'orderStatus',
        'paymentStatus',
        'sales'
    ];

    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [showingMore, setShowingMore] = React.useState<boolean>(false);
    const [orders, setOrders] = React.useState<IOrderResourceShortProps[]>(props.orders || []);
    const [activeOrder, setActiveOrder] = React.useState<IOrderResourceShortProps | undefined>();
    const [orderStatus, setOrderStatus] = React.useState<IOrderStatusResourceShortProps[]>([]);
    const [selectedStatus, setSelectedStatus] = React.useState<string>("active");
    const [selectedHasInvoice, setSelectedHasInvoice] = React.useState<boolean>();
    const [selectedPaymentStatus, setSelectedPaymentStatus] = React.useState<string | undefined>();
    const [pagination, setPagination] = React.useState<IPaginationResourceShort | undefined>();
    const [selectedFilter, setSelectedFilter] = React.useState<FilterOptionProps[]>([]);
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const getPackingListStatus = (item: IOrderResourceProps) => {
        if (!item) return { type: 'none', text: '', countStr: '' };
        const containers = item.containers || [];
        const trucks = item.trucks || [];
        const totalLoads = containers.length + trucks.length;

        if (totalLoads === 0) {
            if (item.goods && item.goods.length > 0) {
                return { type: 'complete', text: 'PL Lengkap', countStr: `(${item.goods.length})` };
            }
            return { type: 'empty', text: 'PL Belum Diisi', countStr: '' };
        }

        const filledContainers = containers.filter(c => c.goods && c.goods.length > 0).length;
        const filledTrucks = trucks.filter(t => t.goods && t.goods.length > 0).length;
        const totalFilled = filledContainers + filledTrucks;

        if (totalFilled === totalLoads) {
            return { type: 'complete', text: 'PL Lengkap', countStr: `(${totalFilled}/${totalLoads})` };
        } else if (totalFilled > 0) {
            return { type: 'partial', text: `PL Parsial (${totalFilled}/${totalLoads})`, countStr: '' };
        } else {
            return { type: 'empty', text: 'PL Belum Diisi', countStr: `(0/${totalLoads})` };
        }
    };

    const columns: IColumn[] = [
        {
            key: "orderNumber",
            name: "Order Number",
            fieldName: "orderNumber",
            minWidth: 135,
            maxWidth: 145,
            onRender: (item: IOrderResourceProps) => {
                if (!item || !item.orderNumber) return <Text size={'small'}>-</Text>;
                const splitted = (item.orderNumber || '').split('.');
                splitted.shift();
                splitted.shift();

                const plStatus = getPackingListStatus(item);

                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <NavLink to={`/orders/${item.id}`}><Text size={'small'}>#{splitted.join('.')}</Text></NavLink>
                    {plStatus.type !== 'none' && (
                        <div style={{ marginTop: 3 }}>
                            {plStatus.type === 'complete' && (
                                <span style={{
                                    fontSize: '10px',
                                    fontWeight: 500,
                                    color: '#15803d',
                                    backgroundColor: '#f0fdf4',
                                    border: '1px solid #bbf7d0',
                                    borderRadius: '10px',
                                    padding: '1px 6px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    whiteSpace: 'nowrap'
                                }}>
                                    <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#22c55e', display: 'inline-block' }} />
                                    {plStatus.text} {plStatus.countStr}
                                </span>
                            )}
                            {plStatus.type === 'partial' && (
                                <span style={{
                                    fontSize: '10px',
                                    fontWeight: 500,
                                    color: '#b45309',
                                    backgroundColor: '#fffbe8',
                                    border: '1px solid #fef08a',
                                    borderRadius: '10px',
                                    padding: '1px 6px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    whiteSpace: 'nowrap'
                                }}>
                                    <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#eab308', display: 'inline-block' }} />
                                    {plStatus.text}
                                </span>
                            )}
                            {plStatus.type === 'empty' && (
                                <span style={{
                                    fontSize: '10px',
                                    fontWeight: 500,
                                    color: '#b91c1c',
                                    backgroundColor: '#fef2f2',
                                    border: '1px solid #fecaca',
                                    borderRadius: '10px',
                                    padding: '1px 6px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    whiteSpace: 'nowrap'
                                }}>
                                    <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#ef4444', display: 'inline-block' }} />
                                    {plStatus.text} {plStatus.countStr}
                                </span>
                            )}
                        </div>
                    )}
                </Stack>
            }
        },
        {
            key: "customer",
            name: "Customer",
            fieldName: "customer",
            minWidth: 150,
            maxWidth: 150,
            isMultiline: true,
            onRender: (item: IOrderResourceProps) => {
                if (!item?.customer) return <Text size={'small'}>-</Text>;
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <NavLink to={`/users/customers/${item.customer.id}`}>
                        <Text size={'small'}>{item.customer.name}</Text>
                    </NavLink>
                    {item.company ? <NavLink to={`/users/companies/${item.company.id}`}>
                        <Text size={'xsmall'}>({item.company.name})</Text>
                    </NavLink> : null}
                </Stack>
            }
        },
        {
            key: "sales",
            name: "Sales",
            fieldName: "sales",
            minWidth: 125,
            maxWidth: 125,
            isMultiline: true,
            onRender: (item: IOrderResourceProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }} tokens={{ childrenGap: 3 }}>
                    {item?.sales && item.sales.length > 0 ? item.sales.map((sales) => {
                        if (!sales?.sales) return null;
                        return <NavLink key={sales.sales.id} to={`/users/administrators/${sales.sales.id}`}>
                            <Text size={'small'}>{sales.sales.name}</Text>
                        </NavLink>
                    }) : <Text size={'small'}>-</Text>}
                </Stack>
            }
        },
        {
            key: "batch",
            name: "Batch",
            fieldName: "batch",
            minWidth: 100,
            maxWidth: 100,
            isMultiline: true,
            onRender: (item: IOrderResourceProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>{item?.batch || "-"}</Text>
                </Stack>
            }
        },
        {
            key: "deliveryType",
            name: "Delivery Type",
            fieldName: "deliveryType",
            minWidth: 125,
            maxWidth: 125,
            isMultiline: true,
            onRender: (item: IOrderResourceProps) => {
                const type = DeliveredGoodsType.find((t) => t.key === item?.goodsType);
                return <Stack tokens={{ childrenGap: 3 }} styles={{ root: { padding: '4px 0px' } }}>
                    <Label size={'xsmall'}>{item?.freightCategory?.name || '-'}</Label>
                    <Text size={'xsmall'}>{item?.deliveryType?.name || '-'} - {type?.text || '-'}</Text>
                </Stack>
            }
        },
        {
            key: "deliveryDetails",
            name: "Address",
            minWidth: 150,
            isMultiline: true,
            onRender: (item: IOrderResourceProps) => {
                const originAddrs = item?.originAddresses || [];
                const destAddrs = item?.destinationAddresses || [];
                const origin = originAddrs.length > 0 ? OrderService.getAddress(originAddrs[0].type, originAddrs[0].address, originAddrs[0].city, originAddrs[0].province) : '-';
                const destination = destAddrs.length > 0 ? OrderService.getAddress(destAddrs[0].type, destAddrs[0].address, destAddrs[0].city, destAddrs[0].province) : '-';

                return <Stack tokens={{ childrenGap: 5 }} styles={{ root: { padding: '4px 0px' } }}>
                    <Stack>
                        <Label size={'xsmall'}>Pick-up Address:</Label>
                        {originAddrs.length > 1 ? <ol style={{ padding: '0px 12px', margin: 0 }}>
                            {originAddrs.map((address) => {
                                const text = OrderService.getAddress(address.type, address.address, address.city, address.province);
                                return <Text size={'small'}><li>{text}</li></Text>
                            })}
                        </ol> : null}
                        {originAddrs.length < 2 && originAddrs.length > 0 ? <Text size={'xsmall'}>{origin}</Text> : null}
                    </Stack>
                    <Stack>
                        <Label size={'xsmall'}>Delivery Address:</Label>
                        {destAddrs.length > 1 ? <ol style={{ padding: '0px 12px', margin: 0 }}>
                            {destAddrs.map((address) => {
                                const text = OrderService.getAddress(address.type, address.address, address.city, address.province);
                                return <Text size={'xsmall'}><li>{text}</li></Text>
                            })}
                        </ol> : null}
                        {destAddrs.length < 2 && destAddrs.length > 0 ? <Text size={'xsmall'}>{destination}</Text> : null}
                    </Stack>
                </Stack>
            }
        },
        {
            key: 'loads',
            name: 'Loads',
            minWidth: 250,
            isMultiline: true,
            onRender: (item: IOrderResourceProps) => {
                const containers = item?.containers || [];
                return <Stack horizontal tokens={{ childrenGap: 5 }} wrap>
                    {containers.map((container, idx) => <Tag text={"Kontainer " + (container?.containerNumber || ("#" + (idx + 1)))} size={'small'} />)}
                </Stack>
            }
        },
        {
            key: "price",
            name: "Total Price",
            fieldName: "price",
            minWidth: 115,
            maxWidth: 115,
            onRender: (item: IOrderResourceProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>Rp. {GeneralService.getNumberWithSeparator((item?.totalAmount || 0) + "", '.')}</Text>
                </Stack>
            }
        },
        {
            key: "profit",
            name: "Profit",
            fieldName: "profit",
            minWidth: 115,
            maxWidth: 115,
            onRender: (item: IOrderResourceProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>Rp. {GeneralService.getNumberWithSeparator((item?.nettProfit || 0) + "", '.')}</Text>
                </Stack>
            }
        },
        {
            key: "orderStatus",
            name: "Status",
            fieldName: "orderStatus",
            minWidth: 75,
            maxWidth: 75,
            onRender: (item: IOrderResourceProps) => {
                const statusKey = item?.status?.key || '';
                let tagVariant: TTagVariant = 'warning';
                if (statusKey === 'completed') { tagVariant = 'success'; }
                else if (statusKey === 'cancelled') { tagVariant = 'error'; }
                else if (statusKey === 'active') { tagVariant = 'active'; }

                return <Stack horizontal>
                    <Tag size={'small'} variant={tagVariant} text={item?.status?.name || 'Unknown'} />
                </Stack>
            }
        },
        {
            key: "paymentStatus",
            name: "Paid/Unpaid",
            minWidth: 75,
            maxWidth: 75,
            onRender: (item: IOrderResourceProps) => {
                let tagVariant: TTagVariant = 'error';
                let tagText: string = 'Unpaid';
                if (item?.paymentCompleted) {
                    tagVariant = 'success';
                    tagText = 'Paid'
                }

                return <Stack horizontal>
                    {Number(item?.totalAmount || 0) > 0 ? <Tag variant={tagVariant} text={tagText} size={'small'} /> : <Text size={'small'}>-</Text>}
                </Stack>
            }
        }
    ];

    useEffect(() => {
        if (!props.orders && orderStatus.length < 1) {
            init();
        } else if (!props.orders && orderStatus.length > 0) {
            _onRetrieveOrders();
        } else if (props.orders) {
            setPagination(props.pagination);
            setOrders(props.orders);
            setLoaded(true);
        } else {
            setLoaded(true);
        }
    }, [props.orders, keyword, selectedFilter, selectedStatus, selectedPaymentStatus, selectedHasInvoice]);

    /*useEffect(() => {
        init();
    }, [props]);*/

    const getQueryString = (pageNumber?: number) => {
        const qs: string[] = props.qs ? [...props.qs] : [];
        qs.push(`top=${GlobalConfig.defaultTop}`);
        if (selectedStatus && selectedStatus !== 'all') {
            qs.push(`order_status=${selectedStatus}`);
        }
        if (selectedPaymentStatus) { qs.push(`payment_status=${selectedPaymentStatus}`); }
        if (pageNumber) { qs.push(`page=${pageNumber}`); }
        if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword.trim()}`) }
        if (selectedHasInvoice === true) { qs.push(`has_invoice=1`) }
        if (selectedHasInvoice === false) { qs.push(`has_invoice=0`) }

        const groupedFilter: { key: string, options: FilterOptionProps[] }[] = [];
        selectedFilter.map((sel) => {
            const gfidx = groupedFilter.findIndex((gf) => gf.key === sel.filterby);
            if (gfidx > -1) {
                groupedFilter[gfidx].options.push(sel);
            } else {
                groupedFilter.push({
                    key: sel.filterby,
                    options: [sel]
                });
            }
        });

        groupedFilter.forEach((gf) => {
            if (gf.key === 'start_date' || gf.key === 'end_date') {
                qs.push(`${gf.key}=${gf.options[0].key}`);
            } else {
                qs.push(`${gf.key}=${gf.options.map((opt) => opt.key).join(';')}`);
            }
        });

        return qs.join("&");
    }

    const init = async () => {
        setLoaded(false);
        await _onRetrieveOrderStatus();
        await _onRetrieveOrders();
    }

    const _onRetrieveOrderStatus = async () => {
        try {
            const results = await OrderStatusService.retrieve();
            setOrderStatus(results);
        } catch (e) {
            banner.add({
                key: 'retrieve_order_status_error',
                text: 'Failed to retrieve order status. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onRetrieveOrders = async (pageNumber?: number) => {
        try {
            if (!props.orders) {
                setLoaded(false);
                const qs = getQueryString(pageNumber);
                const results = await OrderService.retrieve(qs);
                setOrders(results.data);
                setPagination(results.pagination);
                setLoaded(true);
            } else {
                const page = pageNumber != undefined ? pageNumber : 1;
                const top = props.pagination?.itemsPerPage ? props.pagination.itemsPerPage : 50;

                let filtered = props.orders;
                if (keyword && keyword.trim() !== '') {
                    const q = keyword.toLowerCase().trim();
                    filtered = props.orders.filter(o =>
                        o.orderNumber?.toLowerCase().includes(q) ||
                        o.customer?.name?.toLowerCase().includes(q) ||
                        o.company?.name?.toLowerCase().includes(q) ||
                        o.batch?.toLowerCase().includes(q)
                    );
                }

                const results = filtered.filter((o, idx) => idx >= ((page - 1) * top) && idx < (page * top));
                setOrders(results);
                if (props.pagination) {
                    setPagination({ ...props.pagination, currentPage: page, total: filtered.length, lastPage: Math.ceil(filtered.length / top) || 1 });
                }
                setLoaded(true);
            }
        } catch (e) {
            banner.add({
                key: 'retrieve_orders_list_error',
                text: 'Failed to retrieve orders list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
            setLoaded(true);
        }
    }

    const searchTimeoutRef = React.useRef<any>(null);

    const _onKeywordChanged = (value?: string) => {
        setLoaded(false);
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            setKeyword(value || "");
        }, 400);
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        if (!props.hideSearch) {
            items.push({
                key: "search",
                onRender: () => {
                    return <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} styles={{ root: { marginRight: 10 } }} />
                }
            });
        }

        if (!props.hideFilter) {
            items.push({
                key: "filter",
                onRender: () => {
                    return <Stack styles={{ root: { marginRight: 10 } }}>
                        <OrderListFilterButton selected={selectedFilter}
                            filters={props.filters}
                            onChange={(selected) => {
                                setSelectedFilter([...selected]);
                            }} />
                    </Stack>
                }
            });
        }

        if (!props.hideCreateOrder && PermissionsService.hasPermission(Permissions.order.create, user.roles)) {
            farItems.push({
                key: "register",
                text: "Create Order",
                iconProps: { iconName: "Add" },
                onRender: () => {
                    return <PrimaryButton text={"Create Order"}
                        iconProps={{ iconName: "Add" }}
                        onClick={() => { setActiveSurface('create') }}
                        styles={{ root: { marginLeft: 10 } }} />;
                }
            });
        }

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        setActiveOrder(undefined);

        if (refresh) { _onRetrieveOrders() }
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        {!props.hideCommandBar ? <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{
                root: {
                    padding: 0,
                    height: 'unset',
                    backgroundColor: 'transparent'
                }
            }}
            ariaLabel="Use left and right arrow keys to navigate between commands" /> : null}
        {selectedFilter.length > 0 ? <SelectedFilter selected={selectedFilter} onChange={(selected) => {
            setSelectedFilter(selected);
        }} /> : null}
        <Stack tokens={{ childrenGap: 10 }}>
            {(pagination && orders.length > 0) ? <Pagination goToPage={_onRetrieveOrders} currentPage={pagination.currentPage} totalPage={pagination.lastPage} text={pagination.total + " order(s) found"} /> : null}
            {props.hideStatusFilter ? null : <Stack tokens={{ childrenGap: 20 }} horizontal wrap>
                {orderStatus.length > 0 ? <PivotButton buttons={[
                    {
                        key: "all",
                        text: "Semua Order",
                        active: selectedStatus === "all",
                        onClick: () => {
                            setSelectedStatus("all");
                        }
                    },
                    ...orderStatus.map((status) => {
                        return {
                            key: status.key,
                            text: status.name,
                            active: selectedStatus === status.key,
                            onClick: () => {
                                setSelectedStatus(status.key);
                            }
                        }
                    })
                ]} /> : null}
                <PivotButton buttons={[
                    {
                        key: "all",
                        text: "All",
                        active: selectedPaymentStatus === undefined,
                        onClick: () => {
                            setSelectedPaymentStatus(undefined);
                        }
                    },
                    {
                        key: "paid",
                        text: "Paid",
                        active: selectedPaymentStatus === 'paid',
                        onClick: () => {
                            setSelectedPaymentStatus('paid');
                        }
                    },
                    {
                        key: "unpaid",
                        text: "Unpaid",
                        active: selectedPaymentStatus === 'unpaid',
                        onClick: () => {
                            setSelectedPaymentStatus('unpaid');
                        }
                    }
                ]} />
                <PivotButton buttons={[
                    {
                        key: "all",
                        text: "All",
                        active: selectedHasInvoice === undefined,
                        onClick: () => {
                            setSelectedHasInvoice(undefined);
                        }
                    },
                    {
                        key: "hasInvoice",
                        text: "Has Invoice",
                        active: selectedHasInvoice === true,
                        onClick: () => {
                            setSelectedHasInvoice(true);
                        }
                    },
                    {
                        key: "noInvoice",
                        text: "No Invoice",
                        active: selectedHasInvoice === false,
                        onClick: () => {
                            setSelectedHasInvoice(false);
                        }
                    }
                ]} />
            </Stack>}
            <Stack className={styles[props.variant || 'card']}>
                {
                    !loaded || (loaded && orders.length > 0) ? (
                        <>
                            <ShimmeredDetailsList
                                setKey="items"
                                items={orders}
                                columns={columns.filter((col) => shownColumns.indexOf(col.key as TOrderColumn) > -1)}
                                selectionMode={SelectionMode.none}
                                enableShimmer={!loaded}
                                onShouldVirtualize={() => false}
                                ariaLabelForShimmer="Content is being fetched"
                                ariaLabelForGrid="Item details" />
                        </>
                    ) : null
                }
                {loaded && orders.length < 1 ? <Stack styles={{ root: { padding: 10 } }}>
                    <Text>Order(s) not found</Text>
                </Stack> : null}
            </Stack>
            {pagination && orders.length > 0 ? <Pagination goToPage={_onRetrieveOrders} currentPage={pagination.currentPage} totalPage={pagination.lastPage} /> : null}
        </Stack>
        {activeSurface === 'create' ? <OrderForm onDismissed={_onSurfaceDismissed} /> : null}
    </Stack>;
};

export default OrdersList;
